import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonSkeletonText,
    IonTitle,
    IonToolbar
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { useMemo } from 'react';
import { format, startOfMonth, subDays, subMonths } from 'date-fns';
import { useDevicesQuery } from '../query/devicesQuery';
import {
    useCatActivityLeaderboardQuery,
    useDeviceActivityStatsQuery,
    useHouseholdActivityStatsQuery,
    useHouseholdMonthlyActivityStatsQuery
} from '../query/activityStatsQuery';
import { useRfidProfilesQuery } from '../query/rfidProfileQuery';
import {
    monthlyTransitCounts,
    summarizeCatActivity,
    summarizeDeviceDailyStats
} from '../utils/activityStatsSummary';
import { UNIDENTIFIED_RFID_CODE } from '../models/ActivityStats';
import { userLocale } from '../utils/statsFormat';
import { Device } from '../models/Device';
import { RfidProfile } from '../models/RfidProfile';
import { getRfidDisplayLabel } from '../utils/rfidDisplay';
import StatsSection from '../components/stats/StatsSection';
import CatHoursCard, { CatHoursRow } from '../components/stats/CatHoursCard';
import {
    DenyPeekTiles,
    HourHistogramCard,
    MonthlyTransitsCard,
    PreyCard,
    RecordsList,
    TransitsCard,
    WeekdayCard
} from '../components/stats/StatCards';
import { useComponentTranslation, useModalTranslation } from '../hooks/useTranslation';
import statStyles from '../components/stats/stats.module.css';

const STATS_WINDOW_DAYS = 30;
const STATS_MONTHLY_MONTHS = 12;
/** Rollup request cap (gateway MAX_DEVICE_IDS). */
const MAX_STATS_DEVICE_IDS = 20;
const MAX_CAT_ROWS = 8;

/**
 * Per-device activity stats over the last 30 days (design: Stats Modal
 * Treatments v2) — the same structure as the household modal, scoped to one
 * device. Per-cat rows relate this device to each cat's household-wide
 * transits, so multi-flap households can see which flap each cat prefers.
 */
const DeviceStatsModal = ({ dismiss, deviceId }: { dismiss: (data?: string | null, role?: string) => void; deviceId: string }) => {
    const { t: tModals } = useModalTranslation();
    const { t: tComponents } = useComponentTranslation();

    const deviceIds = useMemo(() => [deviceId], [deviceId]);
    const devicesQuery = useDevicesQuery();
    const householdDeviceIds = useMemo(
        () => (devicesQuery.data?.pages.flat() ?? []).map((device: Device) => device.deviceId).slice(0, MAX_STATS_DEVICE_IDS),
        [devicesQuery.data]
    );

    // Fetch double the window so the previous 30 days feed the trend line
    // (and the prey-free streak's lookback).
    const today = new Date();
    const toDay = format(today, 'yyyy-MM-dd');
    const windowFromDay = format(subDays(today, STATS_WINDOW_DAYS - 1), 'yyyy-MM-dd');
    const fetchFromDay = format(subDays(today, STATS_WINDOW_DAYS * 2 - 1), 'yyyy-MM-dd');
    const monthlyFromDay = format(startOfMonth(subMonths(today, STATS_MONTHLY_MONTHS - 1)), 'yyyy-MM-dd');

    const dayStatsQuery = useHouseholdActivityStatsQuery(deviceIds, fetchFromDay, toDay);
    const deviceRollupQuery = useDeviceActivityStatsQuery(deviceIds, fetchFromDay, toDay);
    const leaderboardQuery = useCatActivityLeaderboardQuery(deviceIds, windowFromDay, toDay);
    // Household-wide totals give the list of cats that never use this device.
    const householdLeaderboardQuery = useCatActivityLeaderboardQuery(householdDeviceIds, windowFromDay, toDay);
    const monthlyQuery = useHouseholdMonthlyActivityStatsQuery(deviceIds, monthlyFromDay, toDay);
    const profilesQuery = useRfidProfilesQuery();

    const stats = useMemo(
        () => (dayStatsQuery.data ? summarizeCatActivity(dayStatsQuery.data.days, windowFromDay, toDay) : null),
        [dayStatsQuery.data, windowFromDay, toDay]
    );

    // Event-grain counters: the honest source for contraband totals/streaks.
    const deviceStats = useMemo(
        () => (deviceRollupQuery.data ? summarizeDeviceDailyStats(deviceRollupQuery.data.days, windowFromDay, toDay) : null),
        [deviceRollupQuery.data, windowFromDay, toDay]
    );

    const monthly = useMemo(
        () => (monthlyQuery.data ? monthlyTransitCounts(monthlyQuery.data.months, toDay) : []),
        [monthlyQuery.data, toDay]
    );

    const profilesByRfid = useMemo(() => {
        const map = new Map<string, RfidProfile>();
        for (const profile of profilesQuery.data ?? []) {
            map.set(profile.rfidCode, profile);
        }
        return map;
    }, [profilesQuery.data]);

    const nameOf = (rfidCode: string) => getRfidDisplayLabel(rfidCode, profilesByRfid.get(rfidCode)?.label);

    const catRows = useMemo<CatHoursRow[]>(() => {
        return (leaderboardQuery.data?.cats ?? [])
            .filter((cat) => cat.rfidCode !== UNIDENTIFIED_RFID_CODE && cat.transitsIn + cat.transitsOut > 0)
            .slice(0, MAX_CAT_ROWS)
            .map((cat) => ({
                rfidCode: cat.rfidCode,
                profile: profilesByRfid.get(cat.rfidCode),
                transits: cat.transitsIn + cat.transitsOut,
                sub: null,
                hourHistogram: cat.hourHistogram
            }));
    }, [leaderboardQuery.data, profilesByRfid]);

    const unidentifiedTransits = useMemo(() => {
        const bucket = leaderboardQuery.data?.cats.find((cat) => cat.rfidCode === UNIDENTIFIED_RFID_CODE);
        return bucket ? bucket.transitsIn + bucket.transitsOut : 0;
    }, [leaderboardQuery.data]);

    // Household cats that never used this device in the window (only worth
    // saying when the household actually has other devices).
    const notSeenNames = useMemo(() => {
        if (householdDeviceIds.length < 2 || !leaderboardQuery.data || !householdLeaderboardQuery.data) {
            return [];
        }
        const seenHere = new Set(catRows.map((row) => row.rfidCode));
        return householdLeaderboardQuery.data.cats
            .filter((cat) => cat.rfidCode !== UNIDENTIFIED_RFID_CODE && cat.transitsIn + cat.transitsOut > 0 && !seenHere.has(cat.rfidCode))
            .map((cat) => nameOf(cat.rfidCode));
    }, [householdDeviceIds.length, leaderboardQuery.data, householdLeaderboardQuery.data, catRows, profilesByRfid]);

    const notSeenText = useMemo(() => {
        if (!notSeenNames.length) {
            return null;
        }
        // Intl.ListFormat is missing from the project's TS lib but present in
        // every supported runtime; fall back to a plain join regardless.
        const ListFormat = (Intl as any).ListFormat;
        const names = ListFormat
            ? new ListFormat(userLocale(), { style: 'long', type: 'conjunction' }).format(notSeenNames)
            : notSeenNames.join(', ');
        return tModals('device_stats.not_seen', { names, count: notSeenNames.length });
    }, [notSeenNames, tModals]);

    const recordRows = useMemo(() => {
        if (!deviceStats) {
            return [];
        }
        return [
            { key: 'events_recorded', label: tModals('device_stats.events_recorded'), value: String(deviceStats.totalEvents) },
            { key: 'human_activity', label: tModals('device_stats.human_activity'), value: String(deviceStats.humanActivityEvents) }
        ];
    }, [deviceStats, tModals]);

    const isLoading = dayStatsQuery.isLoading || deviceRollupQuery.isLoading || leaderboardQuery.isLoading;
    const hasActivity = (stats?.hasActivity ?? false) || (deviceStats?.hasActivity ?? false);

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonTitle>{tModals('device_stats.title')}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            fill="solid"
                            className={statStyles.closeButton}
                            onClick={() => dismiss(null, 'cancel')}
                            aria-label={tModals('device_stats.close')}
                        >
                            <IonIcon slot="icon-only" icon={closeOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen color="light">
                <div className={statStyles.body}>
                    <StatsSection title={tComponents('stats.last_30_days')}>
                        {isLoading && (
                            <>
                                <IonSkeletonText animated className={statStyles.cardSkeleton} />
                                <IonSkeletonText animated className={statStyles.cardSkeleton} />
                            </>
                        )}

                        {!isLoading && stats && deviceStats && !hasActivity && (
                            <div className={`${statStyles.card} ${statStyles.emptyCard}`}>{tComponents('stats.no_activity')}</div>
                        )}

                        {stats && deviceStats && hasActivity && (
                            <>
                                <PreyCard
                                    preyAttempts={deviceStats.preyAttempts}
                                    lastPreyDay={deviceStats.lastPreyDay}
                                    daysSinceLastPrey={deviceStats.daysSinceLastPrey}
                                    noPreyText={tModals('device_stats.no_contraband_device')}
                                />
                                <TransitsCard
                                    transits={stats.transits}
                                    transitsIn={stats.transitsIn}
                                    transitsOut={stats.transitsOut}
                                    trendPct={stats.transitsTrendPct}
                                />
                                <DenyPeekTiles denies={stats.denies} peeks={stats.peeks} />
                            </>
                        )}
                    </StatsSection>

                    {catRows.length > 0 && (
                        <StatsSection title={tModals('device_stats.when_each_cat')}>
                            <CatHoursCard rows={catRows} unidentifiedTransits={unidentifiedTransits} />
                            {notSeenText && <div className={statStyles.footnote}>{notSeenText}</div>}
                        </StatsSection>
                    )}

                    {stats && deviceStats && stats.transits > 0 && (
                        <>
                            <StatsSection title={tModals('device_stats.flap_usage')}>
                                <HourHistogramCard
                                    hourHistogram={stats.hourHistogram}
                                    busiestHour={stats.busiestHour}
                                    coreHours={stats.coreHours}
                                />
                            </StatsSection>

                            <StatsSection title={tComponents('stats.transits_by_weekday')}>
                                <WeekdayCard
                                    weekdayCounts={stats.weekdayCounts}
                                    busiestWeekday={stats.busiestWeekday}
                                    avgTransitsPerDay={stats.avgTransitsPerDay}
                                />
                            </StatsSection>

                            {monthly.length >= 2 && (
                                <StatsSection title={tComponents('stats.transits_by_month')}>
                                    <MonthlyTransitsCard monthly={monthly} />
                                </StatsSection>
                            )}

                            <StatsSection title={tComponents('stats.records')}>
                                <RecordsList
                                    mostActiveDay={stats.mostActiveDay}
                                    daysSinceLastPrey={deviceStats.daysSinceLastPrey}
                                    lookbackDays={STATS_WINDOW_DAYS * 2}
                                    extraRows={recordRows}
                                />
                                <div className={statStyles.footnote}>{tModals('device_stats.records_note')}</div>
                            </StatsSection>
                        </>
                    )}
                </div>
            </IonContent>
        </IonPage>
    );
};

export default DeviceStatsModal;
